import React, { useState, useEffect } from 'react';
import { Form, Select, Input, Row, Col, Button, Modal } from 'antd';
import { useDispatch } from 'react-redux';
import FloatingLabel from '../../../../components/FloatingLabel/FloatingLabel';
import SuccessModal from '../../../../components/SuccessModal/SuccessModal';
import StateSeeder from '../../../../components/StateSeeder/StateSeeder';
import { CloseIcon, ChevronDown, ChevronUp } from '../../../../assets/icons';
import { cityRegex, zipCodeRegex, ResidentialAddressRegex } from '../../../../utils/Validations';
import { updateUserProfile } from '../../../../services/authApi/userApi';
import { toastNotify } from '../../../../utils/toastNotify';
import { changeAddressData } from '../../../../store/reducer/accountSlice';

const EditAddress = (props) => {
  const [address, setAddress] = useState();
  const [aptNumber, setAptNumber] = useState();
  const [city, setCity] = useState();
  const [state, setState] = useState(undefined);
  const [stateName, setStateName] = useState(undefined);
  const [zipCode, setZipCode] = useState();
  const [country, setCountry] = useState('unitedstates');
  const [successOpen, setSuccessOpen] = useState(false);
  const [isEdit, setEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const { addressData } = props;
  const dispatch = useDispatch();
  const [arrowUp, setArrowUp] = useState(false);
  const [isChangedValue, setIsChangedValue] = useState(false);

  useEffect(() => {
    handleSetDefaultData();
  }, [addressData]);

  const handleSetDefaultData = () => {
    if (Object.keys(addressData).length > 0) {
      let { residentialAdd, apartmentNumber, city, zipCode } = addressData;
      form.setFieldsValue({
        address: residentialAdd,
        aptNumber: apartmentNumber,
        city: city,
        state: addressData.state,
        zipcode: zipCode,
      });
      setEdit(true);
    }
  };

  const onEditAddressSubmit = async (values) => {
    setLoading(true);
    const { address, aptNumber, city, zipcode } = values;
    const addressFormData = {
      address: {
        residentialAdd: address,
        city: city,
        state: state ? state?.toString() : addressData?.stateId?.toString(),
        zipCode: zipcode,
      },
    };
    if (aptNumber) addressFormData['address']['apartmentNumber'] = aptNumber;
    try {
      const resp = await updateUserProfile(addressFormData);
      if (!resp.isError) {
        dispatch(
          changeAddressData({
            ...addressFormData.address,
            stateId: state ? state?.toString() : addressData?.stateId?.toString(),
            state: stateName ? stateName : addressData.state,
          })
        );
        props.setClose();
        setSuccessOpen(true);
      } else {
        toastNotify('error', resp?.message);
      }
    } catch (error) {
      const { response } = error;
      toastNotify('error', response?.data?.message);
    } finally {
      setLoading(false);
    }
  };
  const closedSuccessModal = () => {
    setSuccessOpen(false);
  };

  const handleCheckChanges = (values) => {
    if (
      values?.address !== addressData?.residentialAdd ||
      values?.aptNumber !== addressData?.apartmentNumber ||
      values?.city !== addressData?.city ||
      (state && Number(values?.state) !== Number(addressData?.stateId)) ||
      values?.zipcode !== addressData?.zipCode
    ) {
      setIsChangedValue(true);
    } else {
      setIsChangedValue(false);
    }
  };

  return (
    <>
      <Modal
        className='account__modal'
        open={props.setOpen}
        onCancel={() => {
          handleSetDefaultData();
          props.setClose();
        }}
        width={524}
        footer={false}
        closeIcon={<CloseIcon />}
        centered
      >
        <h4 className='mb-32 account__modal__title'>Edit your address</h4>
        <Form
          onFieldsChange={() => handleCheckChanges(form.getFieldValue())}
          onSelect
          name='editAddressForm'
          form={form}
          onFinish={onEditAddressSubmit}
          autoComplete='off'
        >
          <FloatingLabel label='Residential address' inputId='address' inputValue={address} isEdit={isEdit}>
            <Form.Item
              validateTrigger="onBlur"
              name='address'
              rules={[
                {
                  required: true,
                  message: 'Please enter Residential address',
                  pattern: ResidentialAddressRegex,
                },
                {
                  min: 2,
                  max: 49,
                  message: 'Please provide a valid residential address value. Residential address length should be between 2 and 49.',
                },
              ]}
            >
              <Input id='address' value={address} onChange={(e) => setAddress(e.target.value)} />
            </Form.Item>
          </FloatingLabel>
          <FloatingLabel label='C/O, Apt, Suite, Unit' inputId='aptNumber' inputValue={aptNumber} isEdit={isEdit}>
            <Form.Item
              validateTrigger="onBlur"
              name='aptNumber'
              rules={[
                // {
                //   required: true,
                //   message: 'Please enter C/O, Apt, Suite, Unit',
                // },
                {
                  min: 1,
                  max: 32,
                  message: 'Please provide a valid apartment number value. Apartment number length should be between 1 and 32.',
                },
              ]}
            >
              <Input id='aptNumber' value={aptNumber} onChange={(e) => setAptNumber(e.target.value)} />
            </Form.Item>
          </FloatingLabel>
          <FloatingLabel label='City' inputId='city' inputValue={city} isEdit={isEdit}>
            <Form.Item
              validateTrigger="onBlur"
              name='city'
              rules={[
                {
                  required: true,
                  message: 'Enter a valid City.',
                  pattern: new RegExp(cityRegex),
                },
                {
                  min: 2,
                  max: 16,
                  message: 'Please provide a valid city value. City length should be between 2 and 16.',
                },
              ]}
            >
              <Input id='city' value={city} onChange={(e) => setCity(e.target.value)} />
            </Form.Item>
          </FloatingLabel>
          {isEdit && (
            <StateSeeder
              value={state}
              setState={(value) => setState(value)}
              setStateName={(value) => setStateName(value)}
              isEdit={isEdit}
              required={true}
            />
          )}
          <FloatingLabel label='Zip Code' inputId='zipcode' inputValue={zipCode} isEdit={isEdit}>
            <Form.Item
              validateTrigger="onBlur"
              name='zipcode'
              rules={[
                {
                  required: true,
                  message: 'Please enter a valid zip code.',
                  pattern: zipCodeRegex,
                },
              ]}
            >
              <Input
                type='number'
                id='zipcode'
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                onKeyPress={(event) => {
                  if (!/[0-9]/.test(event.key)) {
                    event.preventDefault();
                  }
                }}
              />
            </Form.Item>
          </FloatingLabel>
          <FloatingLabel label='Country' inputId='country' inputValue={country} isEdit={isEdit}>
            <>
              <div id='country-anchor' />
              <Form.Item className='mb-48'>
                <Select
                  id='country'
                  onChange={(value) => setCountry(value)}
                  value={country}
                  getPopupContainer={() => document.getElementById('country-anchor')}
                  onDropdownVisibleChange={() => setArrowUp(!arrowUp)}
                  suffixIcon={arrowUp ? <ChevronUp color='#1A1519' /> : <ChevronDown color='#1A1519' />}
                  disabled
                >
                  <Select.Option value='unitedstates'>United States</Select.Option>
                </Select>
              </Form.Item>
            </>
          </FloatingLabel>
          <Row gutter={[16, 16]}>
            <Col span={24} sm={12}>
              <Form.Item className='mb-0'>
                <Button
                  className='w-100'
                  onClick={() => {
                    props.setClose();
                    handleSetDefaultData();
                  }}
                >
                  Cancel
                </Button>
              </Form.Item>
            </Col>
            <Col span={24} sm={12}>
              <Form.Item className='mb-0' shouldUpdate>
                {() => (
                  <Button
                    className='w-100'
                    type='primary'
                    htmlType='submit'
                    loading={loading}
                    disabled={
                      // !form.isFieldsTouched() ||
                      !!form.getFieldsError().filter(({ errors }) => errors.length).length || !isChangedValue
                    }
                  >
                    Save
                  </Button>
                )}
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
      <SuccessModal
        setOpen={successOpen}
        setClose={() => closedSuccessModal()}
        setDone={() => {
          closedSuccessModal(false);
        }}
      />
    </>
  );
};

export default EditAddress;
